(function(global) {
  'use strict';

  var MAX_RECOMMENDATION_URL_LENGTH = 7000;
  var SAFE_HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  var SAFE_QUANTITY = /^[1-9]\d*$/;
  var SAFE_VARIANT_ID = /^(?:\d{1,20}|gid:\/\/shopify\/ProductVariant\/\d{1,20})$/;

  function normalizedStrings(values, validator, label) {
    return values.map(function(value) {
      var normalized = String(value == null ? '' : value).trim();
      if (!validator.test(normalized)) throw new Error('Invalid upsell cart ' + label);
      return normalized;
    });
  }

  function uniqueStrings(values) {
    var unique = [];
    values.forEach(function(normalized) {
      if (unique.indexOf(normalized) === -1) unique.push(normalized);
    });
    return unique;
  }

  function sameStrings(left, right) {
    return left.length === right.length && left.every(function(value, index) {
      return value === right[index];
    });
  }

  function parseFingerprintPart(part) {
    if (typeof part !== 'string' || !part || part.length > 300) {
      throw new Error('Invalid upsell cart fingerprint');
    }

    var match = /^([tc]):(\d{1,20}|gid:\/\/shopify\/ProductVariant\/\d{1,20}):([^:]+):([^:]+)$/.exec(part);
    if (!match
      || !SAFE_VARIANT_ID.test(match[2])
      || !SAFE_QUANTITY.test(match[3])
      || !Number.isSafeInteger(Number(match[3]))
      || !SAFE_HANDLE.test(match[4])) {
      throw new Error('Invalid upsell cart fingerprint');
    }

    return {
      type: match[1],
      variantId: match[2],
      handle: match[4],
    };
  }

  function normalize(raw) {
    if (!raw || raw.complete !== true) throw new Error('Upsell cart identity is incomplete');
    if (!Array.isArray(raw.variantIds)
      || !Array.isArray(raw.cartHandles)
      || !Array.isArray(raw.representedHandles)
      || !Array.isArray(raw.fingerprintParts)) {
      throw new Error('Invalid upsell cart identity structure');
    }

    var normalizedVariantIds = normalizedStrings(raw.variantIds, SAFE_VARIANT_ID, 'variant ID');
    var normalizedCartHandles = normalizedStrings(raw.cartHandles, SAFE_HANDLE, 'handle');
    var normalizedRepresentedHandles = normalizedStrings(raw.representedHandles, SAFE_HANDLE, 'handle');
    var fingerprintVariantIds = [];
    var fingerprintCartHandles = [];
    var fingerprintRepresentedHandles = [];
    var handlesByVariantId = Object.create(null);
    var fingerprintParts = raw.fingerprintParts.map(function(part, index) {
      var parsed = parseFingerprintPart(part);
      if (index === 0 && parsed.type !== 't') throw new Error('Invalid upsell cart fingerprint');
      if (handlesByVariantId[parsed.variantId] && handlesByVariantId[parsed.variantId] !== parsed.handle) {
        throw new Error('Invalid upsell cart fingerprint');
      }
      handlesByVariantId[parsed.variantId] = parsed.handle;
      if (parsed.type === 't') {
        fingerprintVariantIds.push(parsed.variantId);
        fingerprintCartHandles.push(parsed.handle);
      }
      fingerprintRepresentedHandles.push(parsed.handle);
      return part;
    });

    if (!normalizedVariantIds.length
      || !normalizedCartHandles.length
      || !normalizedRepresentedHandles.length
      || !fingerprintParts.length) {
      throw new Error('Upsell cart identity is incomplete');
    }
    if (!sameStrings(normalizedVariantIds, fingerprintVariantIds)
      || !sameStrings(normalizedCartHandles, fingerprintCartHandles)
      || !sameStrings(normalizedRepresentedHandles, fingerprintRepresentedHandles)) {
      throw new Error('Upsell cart identity does not match fingerprint');
    }

    return {
      variantIds: uniqueStrings(normalizedVariantIds),
      cartHandles: uniqueStrings(normalizedCartHandles),
      representedHandles: uniqueStrings(normalizedRepresentedHandles),
      fingerprint: fingerprintParts.join('|'),
    };
  }

  function parseElement(element) {
    if (!element) throw new Error('Upsell cart identity element is missing');
    return normalize(JSON.parse(element.textContent || ''));
  }

  function buildRecommendationUrl(apiBase, identity) {
    var url = String(apiBase || '').replace(/\/$/, '') + '/api/upsell/recommendations'
      + '?variant_ids=' + encodeURIComponent(identity.variantIds.join(','))
      + '&cart_handles=' + encodeURIComponent(identity.cartHandles.join(','));
    if (url.length > MAX_RECOMMENDATION_URL_LENGTH) {
      throw new Error('Upsell recommendation URL is too long');
    }
    return url;
  }

  function createDrawerRefreshController() {
    var currentGeneration = 0;
    var cachedFingerprint = '';
    var cachedData = null;

    function invalidate() {
      currentGeneration += 1;
      cachedFingerprint = '';
      cachedData = null;
    }

    function refresh(handlers) {
      currentGeneration += 1;
      var generation = currentGeneration;
      handlers.onStart();

      return Promise.resolve()
        .then(handlers.loadIdentity)
        .then(function(identity) {
          if (generation !== currentGeneration) return null;
          if (!identity || !identity.fingerprint) {
            throw new Error('Upsell cart identity is incomplete');
          }
          if (cachedData !== null && identity.fingerprint === cachedFingerprint) {
            return { data: cachedData, identity: identity, reused: true };
          }

          return Promise.resolve()
            .then(function() { return handlers.loadRecommendations(identity); })
            .then(function(data) {
              if (generation !== currentGeneration) return null;
              cachedFingerprint = identity.fingerprint;
              cachedData = data;
              return { data: data, identity: identity, reused: false };
            });
        })
        .then(function(result) {
          if (generation !== currentGeneration || !result) return;
          handlers.onSuccess(result.data, result.identity, { reused: result.reused });
        })
        .catch(function(error) {
          if (generation !== currentGeneration) return;
          handlers.onError(error);
        });
    }

    return {
      refresh: refresh,
      invalidate: invalidate,
    };
  }

  global.ZollieUpsellCartIdentity = {
    parseElement: parseElement,
    normalize: normalize,
    buildRecommendationUrl: buildRecommendationUrl,
    createDrawerRefreshController: createDrawerRefreshController,
  };
})(window);
